import { htmlSafe } from '@ember/template';
import { SafeString } from '@ember/template/-private/handlebars';
import { isBlank, isNone } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import {
  InAppExpirationType,
  InAppMessageConfig,
  IN_APP_DEFAULT_MESSAGE_PRIORITY,
  InAppMessageDeliveryPriority,
  ModalPosition,
  InAppMessageExpiration,
  InAppPageRule,
  RuleOperator,
  DisplayMode,
  OverlayPosition,
  PluralizedUnit,
  singularize,
  InAppPlatform,
} from 'cio/utils/in-app/message-config';

/* eslint-disable @typescript-eslint/naming-convention */

// this is the structure backend uses for template.bodyJson
export interface InAppTemplateBody {
  message_id?: string;
  priority?: InAppMessageDeliveryPriority;
  expiration?: {
    type: InAppExpirationType;
    value: number | string;
    unit?: PluralizedUnit;
  };
  route_rules?: {
    android?: { operator: RuleOperator; value: string; order?: number }[];
    ios?: { operator: RuleOperator; value: string; order?: number }[];
    web?: { operator: RuleOperator; value: string; order?: number }[];
  };
  display?: { mode: DisplayMode; value: string; overlay_color: string; message_width: number };
  position?: ModalPosition;
  persistent?: boolean;
  attributes?: Record<string, string>;
  carta_content?: string;
}

/* eslint-enable @typescript-eslint/naming-convention */

const defaultPositionFor = (mode: DisplayMode) => {
  switch (mode) {
    case DisplayMode.Modal:
      return ModalPosition.Top;
    case DisplayMode.Overlay:
      return OverlayPosition.TopCenter;
    default:
      return '';
  }
};

const defaultMessageOverlayColor = '';
const defaultMessageWidth = 0;

type DisplayValue = ModalPosition | OverlayPosition | string;

export class MessageDisplay {
  @tracked mode = DisplayMode.Modal;
  @tracked value: DisplayValue = ModalPosition.Top;
  @tracked overlayColor: string;
  @tracked messageWidth: number;

  constructor(mode: DisplayMode, value: DisplayValue, overlayColor: string, messageWidth: number) {
    this.mode = mode;
    this.value = value;
    this.overlayColor = overlayColor;
    this.messageWidth = messageWidth;
  }

  static deserialize(modeInput?: string, valueInput?: string, overlayColorInput?: string, messageWidthInput?: number) {
    if (modeInput == null) {
      return MessageDisplay.default();
    }
    let mode = modeInput as DisplayMode;
    let value = valueInput ?? defaultPositionFor(mode);
    let overlayColor = overlayColorInput ?? defaultMessageOverlayColor;
    let messageWidth = messageWidthInput ?? defaultMessageWidth;
    return new MessageDisplay(mode, value, overlayColor, messageWidth);
  }

  static default() {
    return MessageDisplay.modal(ModalPosition.Top, defaultMessageOverlayColor, defaultMessageWidth);
  }

  static modal(position: ModalPosition, overlayColor?: string, messageWidth?: number) {
    return new MessageDisplay(
      DisplayMode.Modal,
      position,
      overlayColor ?? defaultMessageOverlayColor,
      messageWidth ?? defaultMessageWidth,
    );
  }

  static overlay(position: OverlayPosition, messageWidth?: number) {
    return new MessageDisplay(
      DisplayMode.Overlay,
      position,
      defaultMessageOverlayColor,
      messageWidth ?? defaultMessageWidth,
    );
  }

  static inline(elementId: string) {
    return new MessageDisplay(DisplayMode.Inline, elementId, defaultMessageOverlayColor, defaultMessageWidth);
  }

  serialize() {
    let { mode, value } = this;
    return {
      mode,
      value,
      overlay_color: this.overlayColor,
      message_width: this.messageWidth,
    };
  }

  equals(other?: MessageDisplay) {
    if (other == null) {
      return false;
    }
    let {
      mode: otherMode,
      value: otherValue,
      overlayColor: otherOverlayColor,
      messageWidth: otherMessageWidth,
    } = other;
    return (
      this.mode === otherMode &&
      this.value === otherValue &&
      this.overlayColor === otherOverlayColor &&
      this.messageWidth === otherMessageWidth
    );
  }

  get errors(): string[] {
    if (this.mode === DisplayMode.Inline) {
      if (this.value == null || this.value.length === 0) {
        return ["Element ID can't be blank"];
      } else if (typeof this.value === 'string' && this.value.startsWith('#')) {
        return ['Use only the element ID. Do not include any selectors (#)'];
      }
    }
    return [];
  }

  get hasErrors() {
    return this.errors.length > 0;
  }
}

export class InAppMessageAttribute {
  key: string;
  value: string;

  constructor(key: string, value?: string) {
    this.key = key;
    this.value = value ?? '';
  }

  get isBlank() {
    return isBlank(this.value);
  }

  get errors(): Record<string, (string | SafeString)[]> {
    return this.isBlank ? { value: [htmlSafe(`Message parameter <b>${this.key}</b>'s value can't be blank.`)] } : {};
  }

  get hasErrors() {
    return Object.keys(this.errors).length > 0;
  }
}

function ensureArray(value: unknown) {
  return Array.isArray(value) ? value : [value];
}

// This function only validates the beggining and the end of the expression (brackets)
// because the backend previously checked that the passed liquid was valid as the expiration
function hasLiquidBrackets(expression: string) {
  let pattern = /^{{.+}}$/;
  return pattern.test(expression);
}

export default class InAppMessage {
  readonly originalMessageId;
  readonly originalExpiration?: InAppMessageExpiration;
  readonly originalPriority?: InAppMessageDeliveryPriority;
  readonly originalDisplay?: MessageDisplay;
  readonly originalRouteRules: InAppPageRule[] = [];
  readonly originalAttributes: InAppMessageAttribute[] = [];
  readonly originalPersistent: boolean = false;
  readonly originalCartaContent;

  @tracked messageId = '';
  @tracked expiration: InAppMessageExpiration | undefined;
  @tracked display = MessageDisplay.default();
  @tracked priority = IN_APP_DEFAULT_MESSAGE_PRIORITY;
  @tracked routeRules: InAppPageRule[] = [];
  @tracked attributes: InAppMessageAttribute[] = [];
  @tracked persistent = false;
  @tracked cartaContent = '';

  // keep history of attributes' values for every selected message to
  // create a better user experience and prevent fields to get cleaned
  // by reloading selected message
  attributesHistory = new Map<string, InAppMessageAttribute[]>();

  static deserialize(bodyJson: string) {
    let config: InAppTemplateBody = {};
    try {
      config = JSON.parse(bodyJson) as InAppTemplateBody;
    } catch {
      // invalid format on template.bodyJson.
      config = {};
    }
    return new InAppMessage(config);
  }

  constructor(data: InAppTemplateBody) {
    this.originalMessageId = data.message_id ?? '';
    this.originalPriority = data.priority ?? IN_APP_DEFAULT_MESSAGE_PRIORITY;
    this.originalPersistent = data.persistent ?? false;
    this.originalCartaContent = data.carta_content ?? '';

    let { position, display: displayData } = data;
    let display: MessageDisplay;
    if (position != null) {
      // backward compatibility
      display = MessageDisplay.modal(position, defaultMessageOverlayColor, defaultMessageWidth);
    } else {
      display = MessageDisplay.deserialize(
        displayData?.mode,
        displayData?.value,
        displayData?.overlay_color,
        displayData?.message_width,
      );
    }

    this.originalDisplay = display;
    this.messageId = this.originalMessageId;
    this.priority = this.originalPriority;
    this.display = this.originalDisplay;
    this.persistent = this.originalPersistent;
    this.cartaContent = this.originalCartaContent;

    if (!isNone(data.expiration)) {
      let { type, value, unit } = data.expiration;
      let singularUnit = unit != null ? singularize(unit) : undefined;
      if (typeof value === 'string' && hasLiquidBrackets(value)) {
        this.originalExpiration = this.expiration = new InAppMessageExpiration({
          type,
          liquidExpression: value,
          unit: singularUnit,
        });
      } else {
        this.originalExpiration = this.expiration = new InAppMessageExpiration({
          type,
          value: Number(value),
          unit: singularUnit,
        });
      }
    }

    if (!isNone(data.attributes)) {
      this.attributes = [
        ...Object.keys(data.attributes).map((key) => new InAppMessageAttribute(key, data.attributes![key])),
      ];
      this.originalAttributes = [...this.attributes];
    }

    if (!isNone(data.route_rules)) {
      let { android, ios, web } = data.route_rules;

      let routeRules = [];
      if (!isNone(android)) {
        android = ensureArray(android);
        routeRules.push(
          ...android.map((rule) => {
            return { ...rule, platform: 'android' };
          }),
        );
      }
      if (!isNone(ios)) {
        ios = ensureArray(ios);
        routeRules.push(
          ...ios.map((rule) => {
            return { ...rule, platform: 'ios' };
          }),
        );
      }
      if (!isNone(web)) {
        web = ensureArray(web);
        routeRules.push(
          ...web.map((rule) => {
            return { ...rule, platform: 'web' };
          }),
        );
      }

      routeRules.sort((a, b) => {
        // backward compatibility
        a.order = a.order ?? 0;
        b.order = b.order ?? 0;
        return a.order - b.order;
      });
      this.originalRouteRules = routeRules.map(
        (rule) => new InAppPageRule(rule.operator, rule.value, rule.platform as InAppPlatform),
      );
      this.routeRules = routeRules.map(
        (rule) => new InAppPageRule(rule.operator, rule.value, rule.platform as InAppPlatform),
      );
    }
  }

  configure(config: InAppMessageConfig) {
    this.expiration = config.expiration;
    this.display = config.display ?? MessageDisplay.default();
    this.priority = config.priority ?? IN_APP_DEFAULT_MESSAGE_PRIORITY;
    this.routeRules = config.routeRules ?? [];
    this.persistent = config.persistent ?? false;
  }

  changeMessage(messageId: string, attributesNames: string[] = []) {
    this.archiveOldMessage();
    let toAttribute = this.getCreateOrRetrieveAttributeFn(messageId);
    this.messageId = messageId;
    this.attributes = [...attributesNames.map((name) => toAttribute(name))];
  }

  changeCartaContent(cartaContent: string) {
    this.cartaContent = cartaContent;
  }

  private archiveOldMessage() {
    let { messageId, attributes } = this;
    this.attributesHistory.set(messageId, [...attributes]);
  }

  private getCreateOrRetrieveAttributeFn(messageId: string) {
    let fn = (name: string) => new InAppMessageAttribute(name);
    if (this.attributesHistory.has(messageId)) {
      fn = (name: string) => this.retrieveAttributeValueFrom(name, this.attributesHistory.get(messageId)!);
    } else if (messageId === this.originalMessageId) {
      fn = (name: string) => this.retrieveAttributeValueFrom(name, this.originalAttributes);
    }
    return fn;
  }

  private retrieveAttributeValueFrom(attributeName: string, attributes: InAppMessageAttribute[]) {
    let attribute = attributes.find((item) => item.key === attributeName);
    if (isNone(attribute)) {
      attribute = new InAppMessageAttribute(attributeName);
    }
    return attribute;
  }

  setAttribute(name: string, value: string) {
    this.attributes = [...this.attributes.filter((attr) => attr.key !== name), new InAppMessageAttribute(name, value)];
  }

  serialize() {
    /* eslint-disable @typescript-eslint/naming-convention */
    let {
      messageId: message_id,
      priority,
      expiration,
      display,
      routeRules,
      persistent,
      cartaContent: carta_content,
    } = this;
    let serializedDisplay = display.serialize();
    let attributes: Record<string, string> = {};
    this.attributes.forEach((attr) => (attributes[attr.key] = attr.value));

    let payload: InAppTemplateBody = {
      message_id,
      priority,
      display: serializedDisplay,
      attributes,
      persistent,
      carta_content,
    };
    if (!isNone(expiration)) {
      payload.expiration = expiration.serialize();
    }

    routeRules.forEach((rule, order) => {
      let { platform } = rule;
      if (isNone(payload.route_rules)) {
        payload.route_rules = {};
      }
      if (isNone(payload.route_rules[platform])) {
        payload.route_rules[platform] = [];
      }

      payload.route_rules[platform]!.push({ ...rule.serialize(), order });
    });

    return JSON.stringify(payload);
  }

  get config(): InAppMessageConfig {
    let { expiration, priority, display, routeRules, persistent } = this;
    return { expiration, priority, display, routeRules, persistent };
  }

  get isDirty() {
    return this.isConfigDirty || this.isMessageDirty;
  }

  private get isMessageDirty() {
    let hasCorrespondentOnCurrentAttributes = (attr: InAppMessageAttribute) =>
      this.attributes.find((item) => item.key === attr.key)?.value === attr.value;
    return (
      this.originalMessageId !== this.messageId ||
      this.originalAttributes.length !== this.attributes.length ||
      this.originalCartaContent !== this.cartaContent ||
      this.originalAttributes.map(hasCorrespondentOnCurrentAttributes).some((correspondence) => !correspondence)
    );
  }

  private get isConfigDirty() {
    return (
      this.isDisplayDirty ||
      this.originalPriority !== this.priority ||
      this.isExpirationDirty ||
      this.isRouteRulesDirty ||
      this.isPersistentDirty
    );
  }

  private get isDisplayDirty() {
    if (this.originalDisplay == null) {
      return this.display != null;
    }
    return !this.originalDisplay.equals(this.display);
  }

  private get isExpirationDirty() {
    if (isNone(this.originalExpiration)) {
      return !isNone(this.expiration);
    }
    return (
      this.originalExpiration!.type !== this.expiration?.type ||
      this.originalExpiration!.liquidExpression !== this.expiration?.liquidExpression ||
      this.originalExpiration!.liquidUnitRelative !== this.expiration?.liquidUnitRelative ||
      this.originalExpiration!.value !== this.expiration?.value
    );
  }

  private get isRouteRulesDirty() {
    if (this.originalRouteRules.length !== this.routeRules.length) {
      return true;
    }

    for (let i = 0; i < this.originalRouteRules.length; i++) {
      let originalRule = this.originalRouteRules[i];
      let currentRule = this.routeRules[i];
      if (
        originalRule.operator !== currentRule.operator ||
        originalRule.value !== currentRule.value ||
        originalRule.platform !== currentRule.platform
      ) {
        return true;
      }
    }

    return false;
  }

  private get isPersistentDirty() {
    return this.originalPersistent !== this.persistent;
  }

  get hasErrors() {
    return !this.isMessageIdPresent || this.hasAttributeErrors || this.hasConfigErrors;
  }

  get isMessageIdPresent() {
    return !isBlank(this.messageId);
  }

  get hasAttributeErrors() {
    return this.attributes.some((attr) => attr.hasErrors);
  }

  get attributeErrors(): (string | SafeString)[] {
    return this.attributes.flatMap((attr) => attr.errors?.value);
  }

  get hasConfigErrors() {
    return this.hasExpirationErrors || this.display.hasErrors || this.hasRulesErrors;
  }

  private get hasRulesErrors() {
    let { config } = this;
    return (
      !isNone(config.routeRules) &&
      config.routeRules.some((rule) => {
        return rule.hasErrors;
      })
    );
  }

  private get hasExpirationErrors() {
    let { expiration } = this;
    return !isNone(expiration) && expiration.hasErrors;
  }
}
